import { render } from 'preact';
import { useEffect } from 'preact/hooks';
import { ThemeProvider } from '@thesavingsgroup/components/theme/ThemeProvider';
import CircularLoader from '@thesavingsgroup/components/CircularLoader/CircularLoader';
import { observeResizeAndMutation } from '@thesavingsgroup/components/helpers/observeResizeAndMutation';
import { getRandomDelay } from '@thesavingsgroup/rate-table/getRandomDelay';
import { QueryParamsProvider, useQueryParams } from './hooks/useQueryParams';
import RateCalculator from './components/RateCalculator/RateCalculator';
import Error from './components/Error/Error';
import { useConfiguration } from './hooks/useConfiguration';
import { useAnalytics } from './hooks/useAnalytics';
import { RateTableProvider } from './hooks/useRateTable/useRateTable';
import type { Theme } from '@thesavingsgroup/components/theme/themes';
import get from 'lodash/get';

const appElement = document.getElementById('app')!;

const Main = () => {
  const { configuration, loading, error } = useConfiguration();
  const { sendLandedWithParamters } = useAnalytics();
  const queryParams = useQueryParams();

  const theme: Theme = {
    experienceBackgroundColor: get(
      queryParams,
      'rateCalculator.backgroundColor',
    ),
    primaryColor: get(queryParams, 'rateCalculator.primaryColor'),
    secondaryColor: get(queryParams, 'rateCalculator.secondaryColor'),

    ...(get(queryParams, 'rateCalculator.padding') === false && {
      experiencePadding: '0',
    }),
  };

  useEffect(() => {
    return appElement
      ? observeResizeAndMutation({ iframeId: window.name })(appElement)
      : () => {};
  }, [appElement]);

  if (loading) {
    return <CircularLoader fullscreen />;
  }

  // IMPORTANT, we do not want to send this event until we have the configuration
  // or we have an error, otherwise we will end up with multiple fires for the same event
  sendLandedWithParamters(window.location, queryParams);

  if (error || !configuration) {
    return <Error />;
  }

  return (
    <ThemeProvider theme={theme}>
      <RateTableProvider
        brandsToDisplay={configuration.brandsToDisplay}
        delay={getRandomDelay()}
      >
        <RateCalculator configuration={configuration} />
      </RateTableProvider>
    </ThemeProvider>
  );
};

// if we are in an iframe, we want to hide the scrollbar
// see https://developer.mozilla.org/en-US/docs/Web/API/Window/parent
// NOTE: If a window does not have a parent, its parent property is a reference to itself
if (window.parent !== window) {
  document.body.style.overflow = 'hidden';
}

render(
  <QueryParamsProvider>
    <Main />
  </QueryParamsProvider>,
  appElement,
);
